/** ● ALL SYSTEMS OPERATIONAL — header indicator + component popover. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RefreshCw } from "lucide-react";

const DOT = { operational: "bg-[color:var(--st-success)]", warning: "bg-[color:var(--st-warn)]",
  degraded: "bg-[color:var(--st-warn)]", failed: "bg-[color:var(--st-critical)]" };
const TEXT = { operational: "text-[color:var(--st-success)]", warning: "text-[color:var(--st-warn)]",
  degraded: "text-[color:var(--st-warn)]", failed: "text-[color:var(--st-critical)]" };
const LABEL = { operational: "Operational", warning: "Setup pending", degraded: "Degraded", failed: "Failed" };

export default function SystemStatusChip({ compact = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/enterprise-os/system-status"); setData(data); }
    catch { setData((d) => d || { overall: "warning", label: "Status unavailable", components: [] }); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 120000); return () => clearInterval(t); }, []);

  const overall = data?.overall || "operational";
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <button data-testid="os-system-status" aria-label="System status" aria-expanded={open}
          className="h-8 px-2.5 rounded-full border border-[color:var(--st-border)] bg-[color:var(--st-surface)] flex items-center gap-2 text-[11px] font-semibold tracking-[0.08em] uppercase hover:border-[color:var(--st-border-strong)] transition-colors">
          <span className={"relative inline-flex h-2 w-2 rounded-full " + DOT[overall]}>
            {overall === "operational" && <span className={"absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping " + DOT[overall]} />}
          </span>
          {!compact && <span className={TEXT[overall]}>{data?.label || "All systems operational"}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[340px] p-0 border-[color:var(--st-border)]" data-testid="os-system-status-panel">
        <div className="px-4 py-3 border-b border-[color:var(--st-border)] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)]">System status</div>
            <div className={"text-sm font-semibold " + TEXT[overall]}>{data?.label || "…"}</div>
          </div>
          <button onClick={load} aria-label="Refresh status" data-testid="os-system-status-refresh"
            className="h-8 w-8 grid place-items-center rounded-md text-[color:var(--st-text-muted)] hover:bg-[color:var(--st-surface-alt)]">
            <RefreshCw className={"h-3.5 w-3.5 " + (busy ? "animate-spin" : "")} />
          </button>
        </div>
        <ul className="p-2 max-h-[380px] overflow-y-auto">
          {(data?.components || []).map((c) => (
            <li key={c.key}>
              <button onClick={() => { setOpen(false); if (c.route) navigate(c.route); }}
                data-testid={`os-status-${c.key}`}
                className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-[color:var(--st-surface-alt)] text-left">
                <span className={"h-2 w-2 rounded-full shrink-0 " + DOT[c.status]} />
                <span className="flex-1 min-w-0">
                  <span className="block text-[13px] text-[color:var(--st-text)] font-medium">{c.label}</span>
                  <span className="block text-[11px] text-[color:var(--st-text-muted)] truncate">{c.detail}</span>
                </span>
                <span className={"text-[10px] uppercase tracking-[0.1em] font-semibold " + TEXT[c.status]}>{LABEL[c.status]}</span>
              </button>
            </li>
          ))}
        </ul>
        <button onClick={() => { setOpen(false); navigate("/admin/system-health"); }} data-testid="os-status-open-dashboard"
          className="w-full px-4 py-2.5 border-t border-[color:var(--st-border)] text-left text-[12px] font-semibold text-[color:var(--st-info)] hover:bg-[color:var(--st-surface-alt)]">
          Open System Health dashboard →
        </button>
      </PopoverContent>
    </Popover>
  );
}
